import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { SLA, SLAFormData } from '@/types/sla';
import { useToast } from '@/hooks/use-toast';
import { useClientFilter } from './useClientFilter';

export const useSLAs = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { currentClientId, showAllClients, clientLoading } = useClientFilter();

  const {
    data: slas = [],
    isLoading,
    error
  } = useQuery({
    queryKey: ['slas', currentClientId, showAllClients],
    queryFn: async () => {
      let query = supabase
        .from('slas')
        .select(`
          *,
          client:clients(name),
          group:groups(name)
        `);

      // Aplicar filtro de cliente se necessário
      if (currentClientId && !showAllClients) {
        query = query.eq('client_id', currentClientId);
      }

      const { data, error } = await query.order('criado_em', { ascending: false });

      if (error) {
        console.error('Error fetching SLAs:', error);
        throw error;
      }

      return data as SLA[];
    },
    enabled: !clientLoading,
  });

  const sanitizeSLA = <T extends Partial<SLAFormData>>(data: T): T => {
    const cleaned: any = { ...data };
    (['grupo_id', 'client_id', 'prioridade'] as const).forEach((k) => {
      if (cleaned[k] === '') cleaned[k] = null;
    });
    return cleaned;
  };

  const createSLA = useMutation({
    mutationFn: async (slaData: SLAFormData) => {
      const { data, error } = await supabase
        .from('slas')
        .insert([sanitizeSLA(slaData)])
        .select()
        .single();

      if (error) {
        console.error('Error creating SLA:', error);
        throw error;
      }

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['slas'] });
      toast({
        title: "Sucesso",
        description: "SLA criado com sucesso!",
      });
    },
    onError: (error) => {
      console.error('Error creating SLA:', error);
      toast({
        title: "Erro",
        description: "Erro ao criar SLA. Tente novamente.",
        variant: "destructive",
      });
    },
  });

  const updateSLA = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<SLAFormData> }) => {
      const { data: updatedData, error } = await supabase
        .from('slas')
        .update(sanitizeSLA(data))
        .eq('id', id)
        .select()
        .single();

      if (error) {
        console.error('Error updating SLA:', error);
        throw error;
      }

      return updatedData;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['slas'] });
      toast({
        title: "Sucesso",
        description: "SLA atualizado com sucesso!",
      });
    },
    onError: (error) => {
      console.error('Error updating SLA:', error);
      toast({
        title: "Erro",
        description: "Erro ao atualizar SLA. Tente novamente.",
        variant: "destructive",
      });
    },
  });

  const deleteSLA = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('slas')
        .delete()
        .eq('id', id);

      if (error) {
        console.error('Error deleting SLA:', error);
        throw error;
      }

    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['slas'] });
      toast({
        title: "Sucesso",
        description: "SLA removido com sucesso!",
      });
    },
    onError: (error) => {
      console.error('Error deleting SLA:', error);
      toast({
        title: "Erro",
        description: "Erro ao remover SLA. Tente novamente.",
        variant: "destructive",
      });
    },
  });

  return {
    slas,
    isLoading,
    error,
    createSLA,
    updateSLA,
    deleteSLA,
  };
};
